import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';

import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import styled from 'styled-components';

import {
  createActionPlanEmailCampaignMessage,
  deleteActionPlanEmailCampaignMessage,
  getActionPlanEmailCampaignDetails,
  patchActionPlanEmailCampaignDetails,
  patchActionPlanEmailCampaignMessage,
} from 'apis/ActionPlansAPI';
import Button from 'components/lib/Button';
import { ActionPlanDetailsType, ActionPlanEmailCampaignDetailType } from 'types/actionPlans';

import ActionPlanCreateEditEmailCampaignMessageModal, {
  ActionPlanCreateEditEmailCampaignMessageValues,
} from './ActionPlanCreateEditEmailCampaignMessageModal';
import ActionPlanEmailCampaignDetails from './ActionPlanEmailCampaignDetails';
import ActionPlanEmailCampaignEditModal, { ActionPlanEmailCampaignEditValues } from './ActionPlanEmailCampaignEditModal';
import ActionPlanEmailCampaignMessages from './ActionPlanEmailCampaignMessages';
import { mapCampaignDetailsFormToPayload, mapMessageFormToPayload, mapMessages, mapMessageToFormValues } from './utils';

const Section = styled.section`
  border: 1px solid var(--line-gray);
  border-radius: 10px;
  display: flex;
  overflow: hidden;
`;

const MainContent = styled.div`
  flex: 1;
  padding: 20px 30px;
`;

const ActionsDivider = styled.div`
  width: 1px;
  flex-shrink: 0;
  background-image: linear-gradient(to bottom, var(--background-gray-light) 0%, var(--line-gray) 50%, var(--background-gray-light));
`;

const Actions = styled.div`
  width: 255px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Divider = styled.div`
  margin: 18px 0 22px;
  height: 1px;
  background-image: linear-gradient(to right, var(--background-gray-light) 0%, var(--line-gray) 50%, var(--background-gray-light));
`;

const MessageOrderTitle = styled.div`
  margin: 0 0 16px;
  text-align: center;
  font-size: 16px;
  font-family: var(--font-bold);
`;

const MessageList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 0 70px;
`;

const AddMessageWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 20px;
`;

const AddMessageButton = styled(Button)`
  min-width: 250px;
  font-family: var(--font-bold);
`;

const AddMessageIcon = styled(PlusOutlined)`
  color: var(--button-blue);
`;

const EmptyText = styled.div`
  font-size: 14px;
  color: var(--text-secondary);
`;

type Props = {
  actionPlan?: ActionPlanDetailsType;
};

const ActionPlanEmailCampaign = ({ actionPlan }: Props) => {
  const actionPlanId = actionPlan?.id;
  const queryClient = useQueryClient();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isMessageModalOpen, setIsMessageModalOpen] = useState(false);
  const [editingMessageId, setEditingMessageId] = useState<number | null>(null);
  const [isSavingCampaignDetails, setIsSavingCampaignDetails] = useState(false);
  const [isCreatingMessage, setIsCreatingMessage] = useState(false);
  const [isUpdatingMessage, setIsUpdatingMessage] = useState(false);
  const [isDeletingMessage, setIsDeletingMessage] = useState(false);

  const { data } = useQuery<ActionPlanEmailCampaignDetailType | null>({
    queryKey: ['action-plan-email-campaign', actionPlanId],
    queryFn: async () => {
      if (!actionPlanId) {
        return null;
      }

      try {
        return await getActionPlanEmailCampaignDetails(actionPlanId);
      } catch (error: any) {
        if (error?.response?.status === 404) {
          return null;
        }

        throw error;
      }
    },
    enabled: Boolean(actionPlanId),
    refetchOnWindowFocus: false,
  });

  const emailCampaignId = data?.id;
  const campaignDetailsValues = useMemo(
    () => ({
      description: data?.description ?? '',
      trigger: data?.trigger ?? '',
      audience: data?.audience ?? '',
      messagingStrategy: data?.messaging_strategy ?? '',
    }),
    [data?.audience, data?.description, data?.messaging_strategy, data?.trigger]
  );
  const messages = useMemo(() => mapMessages(data?.messages ?? []), [data?.messages]);
  const invalidateEmailCampaign = () => queryClient.invalidateQueries(['action-plan-email-campaign', actionPlanId]);

  const saveCampaignDetails = async (values: ActionPlanEmailCampaignEditValues) => {
    if (!actionPlanId) {
      return null;
    }

    setIsSavingCampaignDetails(true);
    try {
      const response = await patchActionPlanEmailCampaignDetails(actionPlanId, mapCampaignDetailsFormToPayload(values));
      await invalidateEmailCampaign();
      return response;
    } finally {
      setIsSavingCampaignDetails(false);
    }
  };

  const createMessage = async (values: ActionPlanCreateEditEmailCampaignMessageValues) => {
    if (!emailCampaignId) {
      throw new Error('Email campaign not found.');
    }

    setIsCreatingMessage(true);
    try {
      const response = await createActionPlanEmailCampaignMessage(emailCampaignId, mapMessageFormToPayload(values));
      await invalidateEmailCampaign();
      return response;
    } finally {
      setIsCreatingMessage(false);
    }
  };

  const updateMessage = async ({
    messageId,
    values,
  }: {
    messageId: number;
    values: ActionPlanCreateEditEmailCampaignMessageValues;
  }) => {
    setIsUpdatingMessage(true);
    try {
      const response = await patchActionPlanEmailCampaignMessage(messageId, mapMessageFormToPayload(values));
      await invalidateEmailCampaign();
      return response;
    } finally {
      setIsUpdatingMessage(false);
    }
  };

  const deleteMessage = async (messageId: number) => {
    setIsDeletingMessage(true);
    try {
      const response = await deleteActionPlanEmailCampaignMessage(messageId);
      await invalidateEmailCampaign();
      return response;
    } finally {
      setIsDeletingMessage(false);
    }
  };
  const [expandedMessageId, setExpandedMessageId] = useState<number | null>(null);
  const hasInitializedExpandedMessage = useRef(false);
  const editingMessage = useMemo(
    () => messages.find((message) => message.id === editingMessageId) ?? null,
    [editingMessageId, messages]
  );

  const resolvedExpandedMessageId = useMemo(
    () => (messages.some((message) => message.id === expandedMessageId) ? expandedMessageId : null),
    [expandedMessageId, messages]
  );

  useEffect(() => {
    hasInitializedExpandedMessage.current = false;
    setExpandedMessageId(null);
  }, [actionPlanId]);

  useEffect(() => {
    if (hasInitializedExpandedMessage.current || !messages.length) {
      return;
    }

    hasInitializedExpandedMessage.current = true;
    setExpandedMessageId(messages[0].id);
  }, [messages]);

  const handleSaveCampaignDetails = async (values: ActionPlanEmailCampaignEditValues) => {
    await saveCampaignDetails(values);
    setIsEditModalOpen(false);
  };

  const handleSaveMessage = async (values: ActionPlanCreateEditEmailCampaignMessageValues) => {
    if (editingMessage) {
      await updateMessage({ messageId: editingMessage.id, values });
    } else {
      await createMessage(values);
    }

    setIsMessageModalOpen(false);
    setEditingMessageId(null);
  };

  const handleDeleteMessage = editingMessage
    ? async () => {
        await deleteMessage(editingMessage.id);
        setIsMessageModalOpen(false);
        setEditingMessageId(null);
      }
    : undefined;

  return (
    <>
      <Section data-test-id="action-plan-email-campaign">
        <MainContent>
          <ActionPlanEmailCampaignDetails
            description={campaignDetailsValues.description}
            trigger={campaignDetailsValues.trigger}
            audience={campaignDetailsValues.audience}
            messagingStrategy={campaignDetailsValues.messagingStrategy}
          />

          <Divider />
          <MessageOrderTitle>Recommended Message Order</MessageOrderTitle>
          <MessageList>
            {messages.length ? (
              <ActionPlanEmailCampaignMessages
                messages={messages}
                expandedMessageId={resolvedExpandedMessageId}
                onToggleMessage={(messageId) => {
                  setExpandedMessageId((prevExpandedMessageId) => (prevExpandedMessageId === messageId ? null : messageId));
                }}
                onEditMessage={(messageId) => {
                  setEditingMessageId(messageId);
                  setIsMessageModalOpen(true);
                }}
              />
            ) : (
              <EmptyText>No campaign messages available.</EmptyText>
            )}
          </MessageList>
          <AddMessageWrapper>
            <AddMessageButton
              icon={<AddMessageIcon />}
              disabled={!emailCampaignId}
              onClick={() => {
                setEditingMessageId(null);
                setIsMessageModalOpen(true);
              }}
            >
              Add Message
            </AddMessageButton>
          </AddMessageWrapper>
        </MainContent>
        <ActionsDivider aria-hidden="true" />
        <Actions>
          <Button icon={<EditOutlined />} onClick={() => setIsEditModalOpen(true)}>
            Edit Campaign Details
          </Button>
        </Actions>
      </Section>
      <ActionPlanEmailCampaignEditModal
        open={isEditModalOpen}
        values={campaignDetailsValues}
        isSaving={isSavingCampaignDetails}
        onCancel={() => setIsEditModalOpen(false)}
        onSave={handleSaveCampaignDetails}
      />
      <ActionPlanCreateEditEmailCampaignMessageModal
        open={isMessageModalOpen}
        isEditing={Boolean(editingMessage)}
        initialValues={editingMessage ? mapMessageToFormValues(editingMessage) : undefined}
        isSaving={isCreatingMessage || isUpdatingMessage}
        isDeleting={isDeletingMessage}
        onCancel={() => {
          setIsMessageModalOpen(false);
          setEditingMessageId(null);
        }}
        onSave={handleSaveMessage}
        onDelete={handleDeleteMessage}
      />
    </>
  );
};

export default ActionPlanEmailCampaign;
